// ── 홍보물 금액 관리 모달 + 드롭 위치 표시 컴포넌트 ────────────────────────────
import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import {
  overlayStyle, modalBoxStyle, modalHeaderStyle, modalHeaderIconBox, modalHeaderTitleStyle, closeBtnStyle,
  modalBodyStyle, modalFooterStyle, emptyStyle,
  addRowBtnStyle, itemHeaderRow, itemRow, itemInput, dragHandleStyle,
  cancelBtnStyle, savePurpleBtnStyle,
} from './styles';

// 드롭 위치 표시 — 굵은 선 + 양쪽 원형 끝점으로 삽입 위치를 직관적으로 표시
// onDragOver/onDrop을 받아야 인디케이터 위에서 마우스를 놓을 때도 드롭이 정상 처리됨
function DropIndicator({ onDragOver, onDrop }) {
  return (
    <div
      style={{ position: 'relative', height: 12, display: 'flex', alignItems: 'center', margin: '2px 0', zIndex: 10 }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* 왼쪽 원형 끝점 */}
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#7c3aed', flexShrink: 0, boxShadow: '0 0 4px rgba(124,58,237,0.6)' }} />
      {/* 가운데 굵은 선 */}
      <div style={{ flex: 1, height: 3, background: '#7c3aed', boxShadow: '0 0 4px rgba(124,58,237,0.4)' }} />
      {/* 오른쪽 원형 끝점 */}
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#7c3aed', flexShrink: 0, boxShadow: '0 0 4px rgba(124,58,237,0.6)' }} />
    </div>
  );
}

export function ItemManageModal({ open, onClose, onSaved }) {
  const { showToast } = useToast();
  useBodyScrollLock(open);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const [rows, setRows]       = useState([]);
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCompact, setIsCompact] = useState(() => window.innerWidth <= 640);
  const [dragIdx, setDragIdx] = useState(null);  // 드래그 중인 행 인덱스 (렌더링용)
  const [dropIdx, setDropIdx] = useState(null);  // 삽입 위치 인덱스 (렌더링용)
  const dragIdxRef    = useRef(null);       // 드롭 핸들러에서 stale closure 방지용 ref
  const dropIdxRef    = useRef(null);       // 드롭 핸들러에서 stale closure 방지용 ref
  const fromHandleRef = useRef(false);      // 핸들에서 시작한 드래그인지 여부
  const modalBodyRef  = useRef(null);       // 자동 스크롤 대상 컨테이너
  const autoScrollRef = useRef(null);       // requestAnimationFrame id

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiClient.get('/api/admin/promotional-items').then((res) => {
      setLoading(false);
      if (res.ok) setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    });
  }, [open]);

  useEffect(() => {
    function syncCompact() {
      setIsCompact(window.innerWidth <= 640);
    }
    syncCompact();
    window.addEventListener('resize', syncCompact);
    return () => window.removeEventListener('resize', syncCompact);
  }, []);

  function addRow() {
    // 새 항목을 맨 위에 추가
    setRows((prev) => [{ id: null, name: '', price: '', is_active: true, no_shipping: false, custom_steps: [] }, ...prev]);
  }

  function setRow(idx, key, val) {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  }

  // 드래그 중 모달 자동 스크롤 — 상단/하단 60px 영역 진입 시 스크롤
  function checkAutoScroll(clientY) {
    if (!modalBodyRef.current) return;
    const { top, bottom } = modalBodyRef.current.getBoundingClientRect();
    const ZONE = 60;
    const speed = 7;
    if (clientY < top + ZONE) {
      startAutoScroll(-speed);
    } else if (clientY > bottom - ZONE) {
      startAutoScroll(speed);
    } else {
      stopAutoScroll();
    }
  }

  function startAutoScroll(speed) {
    if (autoScrollRef.current) return;
    function step() {
      if (modalBodyRef.current) modalBodyRef.current.scrollBy(0, speed);
      autoScrollRef.current = requestAnimationFrame(step);
    }
    autoScrollRef.current = requestAnimationFrame(step);
  }

  function stopAutoScroll() {
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  }

  // 핸들에서만 드래그 시작 허용
  function handleDragStart(e, idx) {
    if (!fromHandleRef.current) { e.preventDefault(); return; }
    dragIdxRef.current = idx;
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  }

  // 커서가 행의 위쪽 반이면 idx, 아래쪽 반이면 idx+1 위치로 삽입 + 자동 스크롤 체크
  function handleDragOver(e, idx) {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const next = e.clientY < rect.top + rect.height / 2 ? idx : idx + 1;
    dropIdxRef.current = next;
    setDropIdx(next);
    checkAutoScroll(e.clientY);
  }

  // 드롭 시 순서 재배치 — ref로 읽어 stale closure 방지
  function handleDrop(e) {
    e.preventDefault();
    stopAutoScroll();
    const from = dragIdxRef.current;
    const to   = dropIdxRef.current;
    if (from === null || to === null) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    const at = to > from ? to - 1 : to;
    next.splice(at, 0, moved);
    setRows(next);
    dragIdxRef.current = null;
    dropIdxRef.current = null;
    setDragIdx(null);
    setDropIdx(null);
    fromHandleRef.current = false;
  }

  function handleDragEnd() {
    stopAutoScroll();
    dragIdxRef.current = null;
    dropIdxRef.current = null;
    setDragIdx(null);
    setDropIdx(null);
    fromHandleRef.current = false;
  }

  async function handleSave() {
    const validRows = rows.filter((r) => String(r.name || '').trim());
    if (!validRows.length) { showToast('저장할 항목이 없습니다.', 'error'); return; }
    setSaving(true);
    const res = await apiClient.put('/api/admin/promotional-items', {
      items: validRows.map((r) => ({
        id:           r.id || undefined,
        name:         String(r.name).trim(),
        price:        Number(r.price) || 0,
        is_active:    r.is_active !== false,
        no_shipping:  Boolean(r.no_shipping),
        // 배송불필요 아이템 전용 단계 목록 (빈 이름 제거)
        custom_steps: r.no_shipping
          ? (r.custom_steps || []).filter((s) => String(s?.name || '').trim()).map((s) => ({ name: String(s.name).trim() }))
          : [],
      })),
    });
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '저장 실패', 'error'); return; }
    showToast('홍보물 목록이 저장되었습니다.', 'success');
    onSaved();
    onClose();
  }

  if (!open) return null;
  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      <div style={{ ...modalBoxStyle, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={modalHeaderIconBox}>💰</div>
            <span style={modalHeaderTitleStyle}>홍보물 금액 관리</span>
          </div>
          <button style={closeBtnStyle} onClick={onClose}>✕</button>
        </div>
        <div style={{ ...modalBodyStyle, padding: isCompact ? '14px 12px' : modalBodyStyle.padding }} ref={modalBodyRef}>
          {loading
            ? <div style={emptyStyle}>불러오는 중...</div>
            : (
              <>
                {/* 항목 추가 — 상단 배치 */}
                <button style={addRowBtnStyle} onClick={addRow}>+ 항목 추가</button>
                {/* 컬럼 헤더 */}
                {!isCompact && <div style={{ ...itemHeaderRow, marginTop: 8 }}>
                  <span style={{ width: 22, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>적요(홍보물명)</span>
                  <span style={{ width: 90, textAlign: 'right' }}>금액(원)</span>
                  <span style={{ width: 54, textAlign: 'center' }}>활성</span>
                  <span style={{ width: 60, textAlign: 'center', fontSize: 10 }}>배송불필요</span>
                </div>}
                {/* 맨 위 삽입 선 */}
                {dropIdx === 0 && dragIdx !== null && (
                  <DropIndicator onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} />
                )}
                {rows.map((row, idx) => (
                  <React.Fragment key={idx}>
                    <div
                      style={{
                        ...(isCompact ? compactItemRow : itemRow),
                        // 드래그 중인 행: 반투명 + 보라 배경 + 그림자로 "들린" 느낌 표현
                        opacity:    dragIdx === idx ? 0.45 : 1,
                        background: dragIdx === idx ? 'var(--bg-subtle)' : (itemRow.background || ''),
                        boxShadow:  dragIdx === idx ? '0 4px 14px rgba(124,58,237,0.25)' : 'none',
                        outline:    dragIdx === idx ? '2px dashed #a78bfa' : 'none',
                      }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                    >
                      {/* 드래그 핸들 */}
                      <span
                        style={isCompact ? compactDragHandleStyle : dragHandleStyle}
                        onMouseDown={() => { fromHandleRef.current = true; }}
                        onMouseUp={() => { fromHandleRef.current = false; }}
                      >
                        ⠿
                      </span>
                      <input
                        style={isCompact ? { ...itemInput, ...compactNameInputStyle } : { ...itemInput, flex: 1 }}
                        placeholder="홍보물명 입력"
                        value={row.name}
                        onChange={(e) => setRow(idx, 'name', e.target.value)}
                      />
                      <input
                        style={isCompact ? { ...itemInput, ...compactAmountInputStyle } : { ...itemInput, width: 90, textAlign: 'right' }}
                        inputMode="numeric"
                        placeholder="0"
                        value={row.price}
                        onChange={(e) => setRow(idx, 'price', e.target.value.replace(/[^0-9]/g, ''))}
                      />
                      <div style={isCompact ? compactCheckWrapStyle : { width: 54, display: 'flex', justifyContent: 'center' }}>
                        <input
                          type="checkbox"
                          checked={row.is_active !== false}
                          onChange={(e) => setRow(idx, 'is_active', e.target.checked)}
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        {isCompact && <span>활성</span>}
                      </div>
                      {/* 배송불필요 체크박스 */}
                      <div style={isCompact ? compactCheckWrapStyle : { width: 60, display: 'flex', justifyContent: 'center' }}>
                        <input
                          type="checkbox"
                          checked={Boolean(row.no_shipping)}
                          onChange={(e) => setRow(idx, 'no_shipping', e.target.checked)}
                          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#7c3aed' }}
                        />
                        {isCompact && <span>배송불필요</span>}
                      </div>
                    </div>
                    {/* 배송불필요 아이템 전용 업무 단계 편집기 */}
                    {row.no_shipping && (
                      <div style={{ marginLeft: isCompact ? 0 : 22, padding: '6px 8px 8px', background: 'var(--bg-subtle)', borderRadius: 6, marginBottom: 4, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 6 }}>업무 단계 설정</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                          {(row.custom_steps || []).map((s, si) => (
                            <div key={si} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <input
                                style={{ width: 90, fontSize: 11, padding: '3px 7px', border: '1px solid var(--border)', borderRadius: 5, outline: 'none', background: 'var(--bg-card)', color: 'var(--text-sub)' }}
                                placeholder={`단계 ${si + 1}`}
                                value={s.name}
                                onChange={(e) => {
                                  const next = (row.custom_steps || []).map((cs, ci) => ci === si ? { name: e.target.value } : cs);
                                  setRow(idx, 'custom_steps', next);
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => setRow(idx, 'custom_steps', (row.custom_steps || []).filter((_, ci) => ci !== si))}
                                style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}
                              >✕</button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => setRow(idx, 'custom_steps', [...(row.custom_steps || []), { name: '' }])}
                            style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 5, padding: '3px 8px', cursor: 'pointer' }}
                          >+ 단계 추가</button>
                        </div>
                      </div>
                    )}
                    {/* 행 아래 삽입 위치 표시선 */}
                    {dropIdx === idx + 1 && dragIdx !== null && (
                      <DropIndicator onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} />
                    )}
                  </React.Fragment>
                ))}
              </>
            )
          }
        </div>
        <div style={modalFooterStyle}>
          <button style={cancelBtnStyle} onClick={onClose}>닫기</button>
          <button style={savePurpleBtnStyle} disabled={saving} onClick={handleSave}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

const compactItemRow = {
  display: 'grid',
  gridTemplateColumns: '28px minmax(0, 1fr)',
  gap: 8,
  alignItems: 'center',
  marginBottom: 8,
  padding: 10,
  border: '1px solid var(--border)',
  borderRadius: 10,
  background: 'var(--bg-subtle)',
  minWidth: 0,
  boxSizing: 'border-box',
};

const compactDragHandleStyle = {
  ...dragHandleStyle,
  width: 28,
  height: 34,
  borderRadius: 8,
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
};

const compactNameInputStyle = {
  width: '100%',
  minHeight: 40,
  fontSize: 16,
};

const compactAmountInputStyle = {
  gridColumn: '2 / 3',
  width: '100%',
  minHeight: 40,
  fontSize: 16,
  textAlign: 'right',
};

const compactCheckWrapStyle = {
  gridColumn: '2 / 3',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 32,
  color: 'var(--text-sub)',
  fontSize: 12,
  fontWeight: 700,
};
